// S2-CC6: Fetch LLM daily report for cost breakdown
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

export interface ModelBreakdown {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  cost_cents: number;
}

export interface CallerBreakdown {
  calls: number;
  cost_cents: number;
}

// S2-CC15 addition. Bucket of cost-per-eval-type (bare, full, readiness, etc.).
// Shape intentionally matches CallerBreakdown so UI code can treat both pivots
// (by_model / by_eval_type) interchangeably when building donut segments.
export interface EvalTypeBreakdown {
  calls: number;
  cost_cents: number;
}

// S2-CC15 addition. Summary statistics of per-call token counts (input+output)
// plus a histogram of counts bucketed by total tokens. `buckets` is ordered
// small→large so the Histogram component can render left→right directly.
export interface TokenDistribution {
  avg: number;
  median: number;
  p95: number;
  max: number;
  buckets: Array<{ label: string; count: number }>;
}

export interface LLMDailyReport {
  date: string;
  summary: {
    total_calls: number;
    total_cost_cents: number;
    api_key_status: string;
    paused: boolean;
    daily_limit_cents: number;
    remaining_cents: number;
  };
  by_model: Record<string, ModelBreakdown>;
  by_caller: Record<string, CallerBreakdown>;
  // by_eval_type: NEW in S2-CC15. Optional in the interface because older
  // cached responses (or a backend rollback) may not include it — Section 3
  // guards with a null check before rendering.
  by_eval_type?: Record<string, EvalTypeBreakdown>;
  by_job: Record<string, number>;
  // token_distribution: NEW in S2-CC15. Optional for the same reason as
  // by_eval_type. Consumers fall back to a zeroed default when absent.
  token_distribution?: TokenDistribution;
  calls: Array<{
    timestamp: string;
    caller: string;
    model: string;
    type: string;
    input_tokens: number;
    output_tokens: number;
    cached_tokens: number;
    cost_cents: number;
    duration_ms: number;
    job_id?: string;
  }>;
}

export function useLLMReport() {
  const [report, setReport] = useState<LLMDailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    apiFetch<LLMDailyReport>('/v1/llm/daily-report')
      .then(data => { setReport(data); setError(null); })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { report, loading, error, refresh };
}
